// Treasury fiat-transactions module parser — DTO (wire shape) → view
// model. The seam every consumer must respect. Containers / components /
// composables never read a snake_case field directly.

import type {
  FiatTransactionDetailDto,
  FiatTransactionDetailInvestorDto,
  FiatTransactionDetailRechargeSettlementDto,
  FiatTransactionDetailTreasuryAccountDto,
  FiatTransactionDetailWithdrawDestinationBankDto,
  FiatTransactionDetailWithdrawSettlementDto,
  FiatTransactionRowDto,
  FiatTransactionsListResponseDto,
} from './fiatTransactionsContracts'
import {
  FIAT_TRANSACTION_STATUS,
  type FiatTransactionDetail,
  type FiatTransactionDetailInvestor,
  type FiatTransactionDetailRechargeSettlement,
  type FiatTransactionDetailTreasuryBankAccount,
  type FiatTransactionDetailWithdrawDestinationBank,
  type FiatTransactionDetailWithdrawSettlement,
  type FiatTransactionRow,
  type FiatTransactionsListResponse,
} from '../types/fiatTransactionsTypes'

export function parseFiatTransactionRow(dto: FiatTransactionRowDto): FiatTransactionRow {
  return {
    id: String(dto.id ?? ''),
    createdAt: String(dto.createdAt ?? dto.created_at ?? ''),
    status: dto.status ?? FIAT_TRANSACTION_STATUS.COMPLETED,
    transactionType: dto.transactionType,
    transactionReason: dto.transactionReason,
    amount: String(dto.amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    balanceBefore: String(dto.balanceBefore ?? dto.balance_before ?? '0'),
    balanceAfter: String(dto.balanceAfter ?? dto.balance_after ?? '0'),
    referenceType: dto.referenceType,
    referenceId: dto.referenceId ?? dto.reference_id ?? null,
    externalReferenceId: dto.externalReferenceId ?? dto.external_reference_id ?? null,
    idempotencyKey: String(dto.idempotencyKey ?? dto.idempotency_key ?? ''),
    investorId: String(dto.investorId ?? dto.investor_id ?? ''),
    investorName: String(dto.investorName ?? dto.investor_name ?? ''),
    investorEmail: String(dto.investorEmail ?? dto.investor_email ?? ''),
    treasuryAccountId: dto.treasuryAccountId ?? dto.treasury_account_id ?? null,
    treasuryAccountName: dto.treasuryAccountName ?? dto.treasury_account_name ?? null,
    treasuryAccountBankName: dto.treasuryAccountBankName ?? dto.treasury_account_bank_name ?? null,
  }
}

export function parseFiatTransactionsListResponse(
  dto: FiatTransactionsListResponseDto,
): FiatTransactionsListResponse {
  const limit = Number(dto.meta?.limit ?? 0)
  const total = Number(dto.meta?.total ?? 0)
  const page = Number(dto.meta?.page ?? 1)
  const totalPagesRaw = dto.meta?.totalPages ?? dto.meta?.total_pages
  const totalPages =
    totalPagesRaw !== undefined && totalPagesRaw !== null
      ? Number(totalPagesRaw)
      : limit > 0
        ? Math.ceil(total / limit)
        : 0
  return {
    data: (dto.data ?? []).map(parseFiatTransactionRow),
    meta: { total, page, limit, totalPages },
  }
}

function parseInvestor(dto: FiatTransactionDetailInvestorDto): FiatTransactionDetailInvestor {
  return {
    id: String(dto.id ?? ''),
    firstName: dto.firstName ?? dto.first_name ?? null,
    lastName: dto.lastName ?? dto.last_name ?? null,
    email: String(dto.email ?? ''),
    kycStatus: dto.kycStatus ?? dto.kyc_status ?? null,
  }
}

function parseTreasuryBankAccount(
  dto: FiatTransactionDetailTreasuryAccountDto,
): FiatTransactionDetailTreasuryBankAccount {
  return {
    id: String(dto.id ?? ''),
    accountName: String(dto.accountName ?? dto.account_name ?? ''),
    bankName: String(dto.bankName ?? dto.bank_name ?? ''),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    iban: dto.iban ?? null,
  }
}

function parseRechargeSettlement(
  dto: FiatTransactionDetailRechargeSettlementDto,
): FiatTransactionDetailRechargeSettlement {
  return {
    requestId: String(dto.requestId ?? dto.request_id ?? ''),
    status: String(dto.status ?? ''),
    paymentReference: dto.paymentReference ?? dto.payment_reference ?? null,
    approvedBankReference: dto.approvedBankReference ?? dto.approved_bank_reference ?? null,
    approvedAt: dto.approvedAt ?? dto.approved_at ?? null,
    approvedBy: dto.approvedBy ?? dto.approved_by ?? null,
    completedAt: dto.completedAt ?? dto.completed_at ?? null,
    documentUrl: dto.documentUrl ?? dto.document_url ?? null,
    rejectionReason: dto.rejectionReason ?? dto.rejection_reason ?? null,
  }
}

function parseWithdrawDestinationBank(
  dto: FiatTransactionDetailWithdrawDestinationBankDto,
): FiatTransactionDetailWithdrawDestinationBank {
  return {
    bankName: String(dto.bankName ?? dto.bank_name ?? ''),
    accountHolderName: String(dto.accountHolderName ?? dto.account_holder_name ?? ''),
    accountNumberMasked: String(dto.accountNumberMasked ?? dto.account_number_masked ?? ''),
    iban: dto.iban ?? null,
  }
}

function parseWithdrawSettlement(
  dto: FiatTransactionDetailWithdrawSettlementDto,
): FiatTransactionDetailWithdrawSettlement {
  const destination = dto.destinationBankAccount ?? dto.destination_bank_account
  return {
    requestId: String(dto.requestId ?? dto.request_id ?? ''),
    status: String(dto.status ?? ''),
    settlementReference: dto.settlementReference ?? dto.settlement_reference ?? null,
    settledAt: dto.settledAt ?? dto.settled_at ?? null,
    approvedAt: dto.approvedAt ?? dto.approved_at ?? null,
    approvedBy: dto.approvedBy ?? dto.approved_by ?? null,
    rejectionReason: dto.rejectionReason ?? dto.rejection_reason ?? null,
    destinationBankAccount: destination
      ? parseWithdrawDestinationBank(destination)
      : { bankName: '', accountHolderName: '', accountNumberMasked: '', iban: null },
  }
}

export function parseFiatTransactionDetail(dto: FiatTransactionDetailDto): FiatTransactionDetail {
  const treasury = dto.treasuryBankAccount ?? dto.treasury_bank_account
  const recharge = dto.settlement?.recharge
  const withdraw = dto.settlement?.withdraw
  return {
    id: String(dto.id ?? ''),
    createdAt: String(dto.createdAt ?? dto.created_at ?? ''),
    status: dto.status ?? FIAT_TRANSACTION_STATUS.COMPLETED,
    rejectionReason: dto.rejectionReason ?? null,
    transactionType: dto.transactionType,
    transactionReason: dto.transactionReason,
    amount: String(dto.amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    balanceBefore: String(dto.balanceBefore ?? dto.balance_before ?? '0'),
    balanceAfter: String(dto.balanceAfter ?? dto.balance_after ?? '0'),
    referenceType: dto.referenceType,
    referenceId: dto.referenceId ?? dto.reference_id ?? null,
    externalReferenceId: dto.externalReferenceId ?? dto.external_reference_id ?? null,
    idempotencyKey: String(dto.idempotencyKey ?? dto.idempotency_key ?? ''),
    metadata: dto.metadata ?? null,
    investor: parseInvestor(dto.investor),
    treasuryBankAccount: treasury ? parseTreasuryBankAccount(treasury) : null,
    settlement: {
      recharge: recharge ? parseRechargeSettlement(recharge) : null,
      withdraw: withdraw ? parseWithdrawSettlement(withdraw) : null,
    },
  }
}
